/**
 * 세션 결제 상태 화면 — 진행바 + 참여자별 상태 (menu #819 M5)
 *
 * ## "Pagar lo que falta" 를 여기서 흡수한다
 *
 * 프로토타입은 잔액 결제를 별도 화면으로 두었으나, 손님이 그 버튼을 찾는 시점은
 * **이 화면을 보고 있을 때**다 — 누가 안 냈는지 보고 나서 대신 내겠다고 결정한다.
 * 화면을 나눠 두면 그 결정과 행동 사이에 이동이 하나 낀다.
 *
 * ## 남은 금액이 0 이어도 계속 그린다
 *
 * 다 낸 세션에서 화면이 사라지면 손님은 자기가 낸 것이 반영됐는지 확인할 방법을
 * 잃는다. 진행바 100% 는 사라짐이 아니라 **완료 표시**다.
 */
import { classifyBill, summarizeProgress, type BillProgressState } from '@/lib/billProgress';
import type { SessionBill } from '@/lib/api/split-payment';
import type { Currency } from '@/types';
import { formatCurrency } from '@/utils';

interface Props {
  bills: readonly SessionBill[];
  currency: Currency;
  /** 잔액 대신 내기. 남은 금액이 0 이면 그리지 않는다. */
  onPayRemaining?: () => void;
  /** 실제로 흡수 가능한 잔여 Bill 금액. 다른 사람의 미납 Bill은 포함하지 않는다. */
  payRemainingMinor?: number;
  isPayingRemaining?: boolean;
  labels: SessionPaymentProgressLabels;
}

export interface SessionPaymentProgressLabels {
  title: string;
  paid: string;
  inCashier: string;
  pending: string;
  /** 잔여 Bill 처럼 주인이 없는 몫 */
  unassigned: string;
  payRemaining: string;
  complete: string;
}

const STATE_LABEL_KEY: Record<BillProgressState, keyof SessionPaymentProgressLabels> = {
  pagado: 'paid',
  en_caja: 'inCashier',
  pendiente: 'pending',
};

export function SessionPaymentProgress({
  bills,
  currency,
  onPayRemaining,
  payRemainingMinor,
  isPayingRemaining = false,
  labels,
}: Props) {
  if (bills.length === 0) {
    return null;
  }

  const progress = summarizeProgress(bills);
  const isComplete = progress.pendingAmount === 0 && progress.inCashierAmount === 0;
  const payableRemaining = payRemainingMinor === undefined
    ? progress.pendingAmount
    : payRemainingMinor / 100;

  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-testid="session-payment-progress"
    >
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-foreground">{labels.title}</h3>
        <span className="text-xs text-muted-foreground" data-testid="progress-percent">
          {progress.paidPercent}%
        </span>
      </div>

      <div
        className="mb-3 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={progress.paidPercent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-primary transition-[width]"
          style={{ width: `${String(progress.paidPercent)}%` }}
        />
      </div>

      <ul className="flex flex-col gap-1.5">
        {bills.map((bill) => {
          const state = classifyBill(bill);

          return (
            <li
              key={bill.id}
              className="flex items-center justify-between text-sm"
              data-testid={`progress-bill-${bill.id}`}
            >
              <span className="text-foreground">
                {/* 잔여는 주인이 없다 — 이름 대신 "아직 주인 없음" 이라고 말해야
                    누군가 가져가야 한다는 것이 전달된다. */}
                {bill.comensal_name ?? labels.unassigned}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-foreground">
                  {formatCurrency(bill.total_amount, currency)}
                </span>
                <span
                  className={
                    state === 'pagado'
                      ? 'text-xs text-primary'
                      : 'text-xs text-muted-foreground'
                  }
                  data-testid={`progress-state-${bill.id}`}
                >
                  {labels[STATE_LABEL_KEY[state]]}
                </span>
              </span>
            </li>
          );
        })}
      </ul>

      {isComplete ? (
        <p className="mt-3 text-center text-sm text-primary" data-testid="progress-complete">
          {labels.complete}
        </p>
      ) : (
        onPayRemaining !== undefined && payableRemaining > 0 ? (
          <button
            type="button"
            onClick={onPayRemaining}
            disabled={isPayingRemaining}
            className="mt-3 w-full rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
            data-testid="progress-pay-remaining"
          >
            {labels.payRemaining} · {formatCurrency(payableRemaining, currency)}
          </button>
        ) : null
      )}
    </section>
  );
}
